import { Shield, User, Menu, X, Search, Activity as ActivityIcon } from 'lucide-react';
import { useState, lazy, Suspense, Activity, useMemo } from 'react';
import { Outlet, Link, useLocation } from 'react-router';
import { useVertical } from '../contexts/VerticalContext';
import { useShowAds } from '../hooks/useShowAds';
import { useTranslation } from '../i18n';
import { useAuthStore } from '../store/authStore';
import { IconByName } from '../utils/icons';
import { StickyBottomAd } from './ads';
import { CookieConsentBanner } from './CookieConsentBanner';
import { Footer } from './Footer';
import { GoogleOneTap } from './GoogleOneTap';
import { OfflineBanner } from './OfflineBanner';

// Lazily imported for Activity pre-rendering — same chunk as App.tsx's StoryFeedPageLazy
const StoryFeedForPrefetch = lazy(() =>
  import('../verticals/weathr/pages/StoryFeedPage').then((m) => ({ default: m.StoryFeedPage }))
);

export function Layout() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const showAds = useShowAds();
  const vertical = useVertical();

  const storedLocation = (() => {
    try {
      return localStorage.getItem('weather-story-location') || 'melbourne';
    } catch {
      return 'melbourne';
    }
  })();

  // Preserve ?v= param on nav links when using vertical override (dev mode)
  const verticalOverride = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('v');
  }, []);

  const navigation = useMemo(
    () =>
      vertical.navigation.map((item) => {
        let href = item.href.replace('__LOCATION__', storedLocation);
        if (verticalOverride) {
          href += `${href.includes('?') ? '&' : '?'}v=${verticalOverride}`;
        }
        return {
          name: item.name,
          href,
          iconName: item.icon,
          activePrefix: item.activePrefix,
        };
      }),
    [vertical.navigation, storedLocation, verticalOverride]
  );

  const isActive = (itemOrPath: (typeof navigation)[number] | string) => {
    const prefix = typeof itemOrPath === 'string' ? itemOrPath : itemOrPath.activePrefix;
    return prefix === '/' ? location.pathname === '/' : location.pathname.startsWith(prefix);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link
                to={verticalOverride ? `/?v=${verticalOverride}` : '/'}
                className="flex items-center space-x-2"
              >
                <IconByName name={vertical.icon} className="h-8 w-8 text-primary-500" />
                <span className="font-display text-xl font-bold text-gray-100">
                  {vertical.name}
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  viewTransition
                  className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                    isActive(item) ? 'text-primary-400' : 'text-gray-400 hover:text-gray-100'
                  }`}
                >
                  <IconByName name={item.iconName} className="h-4 w-4" />
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>

            {/* User Menu */}
            <div className="hidden md:flex items-center space-x-4">
              <Link
                to="/search"
                className={`text-sm font-medium transition-colors ${
                  isActive('/search') ? 'text-primary-400' : 'text-gray-400 hover:text-gray-100'
                }`}
                title="Search"
              >
                <Search className="h-5 w-5" />
              </Link>
              {isAuthenticated ? (
                <>
                  {user?.isAdmin && (
                    <Link
                      to="/admin"
                      className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                        isActive('/admin') ? 'text-sky-400' : 'text-gray-400 hover:text-gray-100'
                      }`}
                    >
                      <Shield className="h-4 w-4" />
                      <span>{t('nav.admin')}</span>
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 text-sm font-medium text-gray-300"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                      {user?.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt={user.displayName || 'User'}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <User className="h-4 w-4 text-primary-400" />
                      )}
                    </div>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/" className="btn-primary">
                    {t('common.signIn')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-400 hover:text-gray-100"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-700">
            <div className="px-4 py-4 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  viewTransition
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                    isActive(item)
                      ? 'bg-gray-700 text-primary-400'
                      : 'text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  <IconByName name={item.iconName} className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              ))}
              <Link
                to="/search"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                  isActive('/search')
                    ? 'bg-gray-700 text-primary-400'
                    : 'text-gray-400 hover:bg-gray-700'
                }`}
              >
                <Search className="h-5 w-5" />
                <span>Search</span>
              </Link>
              <hr className="my-2 border-gray-700" />
              {isAuthenticated ? (
                <>
                  {user?.isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                        isActive('/admin')
                          ? 'bg-gray-700 text-sky-400'
                          : 'text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      <Shield className="h-5 w-5" />
                      <span>{t('nav.admin')}</span>
                    </Link>
                  )}
                  <Link
                    to="/engagement"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                      isActive('/engagement')
                        ? 'bg-gray-700 text-primary-400'
                        : 'text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    <ActivityIcon className="h-5 w-5" />
                    <span>My Activity</span>
                  </Link>
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-700"
                  >
                    <User className="h-5 w-5" />
                    <span>{t('nav.profile')}</span>
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2 btn-primary text-center"
                  >
                    {t('common.signIn')}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <OfflineBanner />

      {/* Main content — page transitions handled by CSS View Transition API.
          Nav links use viewTransition={true} to trigger startViewTransition,
          which fires the ::view-transition-old/new(root) CSS keyframes. */}
      <main>
        <Outlet />
      </main>

      <Footer />

      {/* React 19 Activity — pre-renders StoryFeedPage while user is on home page.
          Only for Weathr vertical where StoryFeedPage exists. */}
      {vertical.id === 'weathr' && location.pathname === '/' && (
        <Activity mode="hidden">
          <Suspense fallback={null}>
            <StoryFeedForPrefetch />
          </Suspense>
        </Activity>
      )}

      {showAds && <StickyBottomAd />}
      <CookieConsentBanner />
      <GoogleOneTap />
    </div>
  );
}
